// Lead links (PJ 2026-10-09), shared by the lead card, the Business drawer header and the Leads page. Pure; unit-tested
// in tests/fd-lead-links.test.ts.
//   * "Search the area": a Google search for "<niche plural> near <city>, <state or province>", e.g. "plumbers near
//     Toronto, Ontario", so the setter sees who the lead competes with. Shown only when the city is known.
//   * "Find on Google Maps": when a lead has no Google profile link, a Google Maps search for its name and address
//     (free; it opens the profile when one exists).
//   * The phone kind line (migration 0028): whether the number we dial is the store line Google lists or likely the
//     owner's cell. Google's number is information only: it is never a link and never dialled.
//   * The observation line (scripts audit F6): what the lead list saw online, with a Check link, until a setter puts it
//     in the opener (tests/fd-lead-observation.test.ts).
import type { Business } from '../../../src/shared/api';
import type { PhoneKind } from '../../../src/shared/api-core';
import { OPENER_NEVER } from '../../../src/shared/opener-lint';
import { fmtPhone, stateName } from './labels';

/** Niche keys (campaign templates in content/campaigns, the lead engine's niche library in migrations 0010 and 0015) and
 *  the words lists use for them, as the plural phrase a person types into Google. Keys are compared after nicheKey(). */
export const NICHE_SEARCH_PHRASES: Record<string, string> = {
  // campaign templates and the first three niche profiles
  plumbing_hvac: 'plumbers',
  auto_repair: 'auto repair shops',
  dispensary: 'dispensaries',
  // the lead engine's niche library (0015)
  water_damage_restoration: 'water damage restoration companies',
  pest_control: 'pest control companies',
  garage_door: 'garage door repair companies',
  electrician: 'electricians',
  roofing: 'roofers',
  locksmith: 'locksmiths',
  towing: 'towing companies',
  appliance_repair: 'appliance repair services',
  tree_service: 'tree services',
  moving: 'movers',
  painting: 'painters',
  landscaping: 'landscapers',
  personal_injury_law: 'personal injury lawyers',
  family_law: 'family lawyers',
  immigration_law: 'immigration lawyers',
  veterinary: 'veterinarians',
  septic_service: 'septic services',
  // words a CSV or a campaign may use instead of the key
  dispensaries: 'dispensaries',
  cannabis: 'dispensaries',
  cannabis_dispensary: 'dispensaries',
  cannabis_store: 'dispensaries',
  cannabis_retail: 'dispensaries',
  cannabis_retailer: 'dispensaries',
  cannabis_retail_store: 'dispensaries',
  plumbing: 'plumbers',
  plumber: 'plumbers',
  plumbers: 'plumbers',
  plumbing_heating: 'plumbers',
  plumbing_and_heating: 'plumbers',
  hvac: 'HVAC companies',
  auto: 'auto repair shops',
  auto_shop: 'auto repair shops',
  auto_repair_shop: 'auto repair shops',
  car_repair: 'auto repair shops',
  mechanic: 'auto repair shops',
};

/** 'Plumbing & HVAC' -> 'plumbing_hvac'; 'auto-repair' -> 'auto_repair'. */
export function nicheKey(niche: string | null | undefined): string {
  return (niche ?? '').trim().toLowerCase().replace(/&/g, ' ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/** The plural phrase for a niche; an unknown niche falls back to its own label ('tattoo_studio' -> 'tattoo studio'). */
export function nicheSearchPhrase(niche: string | null | undefined): string | null {
  const key = nicheKey(niche);
  if (!key) return null;
  return NICHE_SEARCH_PHRASES[key] ?? key.replace(/_/g, ' ');
}

/** The first niche that is set: the campaign's, then the business's own. */
export function pickNiche(...niches: Array<string | null | undefined>): string | null {
  for (const n of niches) if (nicheKey(n)) return n!.trim();
  return null;
}

/** 'ON' -> 'Ontario', 'CA' in the US -> 'California'; nothing for a blank code. */
export function regionFullName(country: 'US' | 'CA', code: string | null | undefined): string | null {
  return (code ?? '').trim() ? stateName(country, code) : null;
}

export interface AreaPlace { niche: string | null | undefined; city: string | null | undefined; stateProv: string | null | undefined; country: 'US' | 'CA' }

/** "plumbers near Toronto, Ontario". null when the city or the niche is unknown. */
export function areaSearchQuery(p: AreaPlace): string | null {
  const city = (p.city ?? '').replace(/\s+/g, ' ').trim();
  const phrase = nicheSearchPhrase(p.niche);
  if (!city || !phrase) return null;
  const region = regionFullName(p.country, p.stateProv);
  return `${phrase} near ${region ? `${city}, ${region}` : city}`;
}

/** The "Search the area" link: https://www.google.com/search?q=<encoded query>. */
export function areaSearchUrl(p: AreaPlace): string | null {
  const q = areaSearchQuery(p);
  return q ? `https://www.google.com/search?q=${encodeURIComponent(q)}` : null;
}

export interface MapsPlace { name: string | null | undefined; address: string | null | undefined; city: string | null | undefined; stateProv: string | null | undefined; country: 'US' | 'CA' }

/** Google Maps search for the business (https://www.google.com/maps/search/?api=1&query=<name, address, city, state>).
 *  Needs the name and a street address or a city; null otherwise. */
export function mapsSearchUrl(p: MapsPlace): string | null {
  const clean = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();
  const name = clean(p.name);
  const address = clean(p.address);
  const city = clean(p.city);
  if (!name || (!address && !city)) return null;
  // The address cell often carries the city already ("123 Main St, Vancouver"): don't say it twice. Only a whole
  // comma-separated part counts, so "1 Vancouver St" or "12 Hope Rd" (city Hope) still gets its city.
  const cityInAddress = !!city && address.split(',').map((s) => s.trim().toLowerCase()).includes(city.toLowerCase());
  const parts = [name, address, cityInAddress ? '' : city, regionFullName(p.country, p.stateProv) ?? ''].filter(Boolean);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
}

/** What the Google profile slot shows: the stored profile link, else a Maps search, else nothing to open. */
export type ProfileSlot = { kind: 'profile'; href: string } | { kind: 'search'; href: string } | { kind: 'none' };

export function googleProfileSlot(gbpUrl: string | null | undefined, place: MapsPlace): ProfileSlot {
  if (gbpUrl && gbpUrl.trim()) return { kind: 'profile', href: gbpUrl.trim() };
  const search = mapsSearchUrl(place);
  return search ? { kind: 'search', href: search } : { kind: 'none' };
}

/** Hover text on the muted "No website on file" / "No Google link on file" tags: a missing link is not proof they have none. */
export const NO_WEBSITE_TITLE = "No website on file for this lead. Check before you tell them they don't have one.";
export const NO_PROFILE_TITLE = "No Google profile link on file for this lead. Check before you tell them they don't have one.";
export const FIND_ON_MAPS_TITLE = 'Searches Google Maps for this business by name and address; opens their profile when there is one.';
export const PHONE_KIND_TITLE = "From the lead list's check of this number against the store's Google listing. On the dialer, Dial calls the owner's line first and Call the store (A) dials the store line (PJ 2026-10-23).";

/** PJ 2026-10-23 (migration 0029): the owner's number from the licence, when the business's own number is the store line:
 *  "Owner's line (likely cell): (647) 555-0100 (the dialer calls it first)". */
export function ownerNumberLine(n: { e164: string; likelyCell: boolean }): string {
  return `${n.likelyCell ? "Owner's line (likely cell)" : "Owner's line"}: ${fmtPhone(n.e164)} (the dialer calls it first)`;
}

/** The lead card's line about the number we dial (migration 0028); null when unknown or not checked. */
export function phoneKindLine(kind: PhoneKind | null | undefined, googlePhoneE164: string | null | undefined): { tone: 'ok' | 'warn'; text: string } | null {
  if (kind === 'store') return { tone: 'ok', text: 'Store line (matches Google)' };
  if (kind === 'owner_cell_likely') {
    const g = googlePhoneE164 ? fmtPhone(googlePhoneE164) : '';
    return { tone: 'warn', text: g ? `Likely the owner's cell — Google lists ${g} for the store` : "Likely the owner's cell — Google lists a different number for the store" };
  }
  return null;
}

/**
 * The lead card's observation line (scripts audit F6, promised to PJ 2026-10-09): the store's online-presence problem the
 * lead list saw, in its own words, e.g. "Seen online: there's no website link when people find you on Maps", with a
 * Check link to where it was seen. "Use in opener" (POST /api/businesses/:id/verify-observation) marks it checked, and
 * the line becomes "Opener fact": the {observation} the Script panel's observation openers use (script.ts
 * verifiedObservation). A lead the lead engine researched is verified in the Business drawer instead, where its hook's
 * evidence and live status are; one with no web source, or with a word openers never say (OPENER_NEVER), gets no
 * button. null when there is no observation.
 */
export interface ObservationLine {
  label: 'Seen online' | 'Opener fact';
  text: string;
  checkUrl: string | null;   // http(s) only
  verified: boolean;
  canUse: boolean;           // the "Use in opener" button
  note: string | null;       // why there is no button, when an unverified line has none
}

export const OBSERVATION_CHECK_TITLE = 'Opens where this was seen, in a new tab. Check it still holds before you say it.';
export const OBSERVATION_USE_TITLE = 'Only if it still holds. Marks it as checked by you, and the observation openers then use it.';

/**
 * "Use in opener" stays off until the setter clicks Check on this lead: the drawer's rule for Verify (verifyGate) and
 * SETTER-PLAYBOOK §3 (open the source, check it's true today, then use it). `opened` lives in page memory per lead.
 */
export function observationUseGate(opened: boolean, busy: boolean): { enabled: boolean; title: string; hint: string | null } {
  if (busy) return { enabled: false, title: 'Saving…', hint: null };
  if (!opened) return { enabled: false, title: 'Click Check first and make sure it still holds today.', hint: 'Click Check first.' };
  return { enabled: true, title: OBSERVATION_USE_TITLE, hint: null };
}

export function observationLine(
  b: Pick<Business, 'observation' | 'observationSourceUrl' | 'observationVerifiedAt'>,
  opts: { researched?: boolean } = {},
): ObservationLine | null {
  const text = (b.observation ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const checkUrl = webLink(b.observationSourceUrl);
  if (b.observationVerifiedAt) return { label: 'Opener fact', text, checkUrl, verified: true, canUse: false, note: null };
  const base = { label: 'Seen online' as const, text, checkUrl, verified: false };
  if (opts.researched) return { ...base, canUse: false, note: 'Verify it in the Business drawer (I) to use it in the opener.' };
  // The verify endpoint refuses an observation without a web source (a fact without a source is not a fact).
  if (!checkUrl) return { ...base, canUse: false, note: 'No source link, so it stays out of the opener.' };
  // The observation is said inside the opener ("...and saw {observation}"): the Google-listing scam words never are.
  const word = text.match(OPENER_NEVER)?.[0];
  if (word) return { ...base, canUse: false, note: `It says "${word}", which an opener never says. Bring it up later in the call.` };
  return { ...base, canUse: true, note: null };
}

/** An http(s) address with a dotted host, the test the verify endpoint applies (isHttpUrl); null otherwise. */
function webLink(s: string | null | undefined): string | null {
  const v = (s ?? '').trim();
  if (!v || v.length > 2000) return null;
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.') ? v : null;
  } catch {
    return null;
  }
}
